import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValidating(false);
        setIsTokenValid(false);
        return;
      }

      try {
        const response = await authAPI.validateResetToken(token);
        setIsTokenValid(response.data.valid);
        setIsExpired(response.data.reason === 'expired');
      } catch (error) {
        setIsTokenValid(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await authAPI.resetPassword({ token, password });
      setIsSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to reset password. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state while validating token
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="size-12 border-4 border-wrg-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Validating reset link…</p>
        </div>
      </div>
    );
  }

  // Invalid or expired token
  if (!isTokenValid) {
    return (
      <div className="min-h-screen flex">
        {/* Left side - branding */}
        <div className="hidden lg:flex lg:flex-1 bg-wrg-blue-500 items-center justify-center p-12">
          <div className="max-w-md text-center">
            <h1 className="text-4xl font-semibold text-white mb-4">
              WRG Travel
            </h1>
            <p className="text-xl text-blue-200 mb-8">
              World Racing Group Travel Request System
            </p>
          </div>
        </div>

        {/* Right side - error message */}
        <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
          <div className="w-full max-w-md">
            <div className="lg:hidden text-center mb-8">
              <h1 className="text-3xl font-semibold text-wrg-blue-500">WRG Travel</h1>
              <p className="text-gray-600 mt-2">Travel Request System</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center">
                <div className="size-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="size-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                  {isExpired ? 'Link Expired' : 'Invalid Link'}
                </h2>
                <p className="text-gray-600 mb-6">
                  {isExpired
                    ? 'This password reset link has expired. Please request a new one.'
                    : 'This password reset link is invalid or has already been used.'}
                </p>
                <a
                  href="/forgot-password"
                  className="inline-block w-full py-2.5 px-4 bg-wrg-blue-500 text-white font-medium rounded-lg hover:bg-wrg-blue-700 focus:ring-4 focus:ring-wrg-blue-500/50 transition-colors text-center mb-3"
                >
                  Request New Link
                </a>
                <a
                  href="/"
                  className="inline-block text-sm text-wrg-blue-500 hover:text-wrg-blue-600 font-medium"
                >
                  Back to Sign In
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen flex">
        {/* Left side - branding */}
        <div className="hidden lg:flex lg:flex-1 bg-wrg-blue-500 items-center justify-center p-12">
          <div className="max-w-md text-center">
            <h1 className="text-4xl font-semibold text-white mb-4">
              WRG Travel
            </h1>
            <p className="text-xl text-blue-200 mb-8">
              World Racing Group Travel Request System
            </p>
          </div>
        </div>

        {/* Right side - success message */}
        <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
          <div className="w-full max-w-md">
            <div className="lg:hidden text-center mb-8">
              <h1 className="text-3xl font-semibold text-wrg-blue-500">WRG Travel</h1>
              <p className="text-gray-600 mt-2">Travel Request System</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center">
                <div className="size-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="size-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">Password reset</h2>
                <p className="text-gray-600 mb-6">
                  Your password has been successfully reset. You can now sign in with your new password.
                </p>
                <a
                  href="/"
                  className="inline-block w-full py-2.5 px-4 bg-wrg-blue-500 text-white font-medium rounded-lg hover:bg-wrg-blue-700 focus:ring-4 focus:ring-wrg-blue-500/50 transition-colors text-center"
                >
                  Sign In
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen flex">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:flex-1 bg-wrg-blue-500 items-center justify-center p-12">
        <div className="max-w-md text-center">
          <h1 className="text-4xl font-semibold text-white mb-4">
            WRG Travel
          </h1>
          <p className="text-xl text-blue-200 mb-8">
            World Racing Group Travel Request System
          </p>
        </div>
      </div>

      {/* Right side - reset password form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-3xl font-semibold text-wrg-blue-500">WRG Travel</h1>
            <p className="text-gray-600 mt-2">Travel Request System</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Reset Your Password</h2>
            <p className="text-gray-600 mb-6">
              Enter your new password below.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  New password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wrg-blue-500 focus:border-transparent transition-colors"
                  placeholder="At least 8 characters"
                  disabled={isLoading}
                />
                <p className="mt-1 text-sm text-gray-500">Must be at least 8 characters</p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm new password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wrg-blue-500 focus:border-transparent transition-colors"
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 px-4 bg-wrg-blue-500 text-white font-medium rounded-lg hover:bg-wrg-blue-700 focus:ring-4 focus:ring-wrg-blue-500/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin size-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Resetting…
                  </span>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <a
                href="/"
                className="text-sm text-wrg-blue-500 hover:text-wrg-blue-600 font-medium"
              >
                Back to Sign In
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
